// 底栏 template3 = supergol 皮肤（复刻 supergol.today 底栏）：Inicio · Promoção · Depósito(中间凸起金圆) · Saque · Perfil。
// 基于 template2（后台可配套）改：数据来源相同（后台 homeLayout.bottomNavItems 5 项 + 排序），
// 差异：
//   1. 内置回落 5 项换成本套（navKey 含新增的 deposit / withdraw），图标为仓库自带 SVG（src/assets/supergol/nav）；
//   2. 红点：Promoção 挂「待领奖励」红点（vipBadgeCount>0，B4 reward_center 落地后换成聚合数），Perfil 挂站内信未读红点；
//      VIP 入口若被配进来仍沿用数字角标；
//   3. 配色全走语义 token（金色选中态、面板底色），第 3 位凸起为金色圆底（见 SCSS）。
// 第 3 项凸起仍是**按位置**的：后台把 deposit 排在第 3 位即得到竞品形态。
import * as React from "react";
import { useCallback, memo, useMemo } from "react";
import { useGlobalValue } from "@/stores/GlobalState";
import { useVipBadgeCount } from "@/hooks/useVipBadgeCount";
import { useT } from "@/i18n/hooks";
import {
  useHomeBottomNavItems,
  useHomeConfigSettled,
  useHomeBottomNavBackground,
} from "@/modules/homeDisplay/hooks";
import type { BottomNavKey } from "@/modules/homeDisplay/types";
import utils from "@/utils";
import type {
  NavigationItem,
  BottomNavigationProps,
} from "../../BottomNavigation.types";
import "./BottomNavigation.scss";
import { useMessageNotification } from "@/modules/notification";
import { useUserStore } from "@/modules";
import { navigationPolicy } from "@/navigation/policy";
import { openLoginRegisterPopup } from "@/components/LoginRegisterPopup";
import { ROUTES } from "@/types/routes";
import homeImg from "@/assets/supergol/nav/home.svg";
import homeActiveImg from "@/assets/supergol/nav/home-active.svg";
import promoImg from "@/assets/supergol/nav/promo.svg";
import promoActiveImg from "@/assets/supergol/nav/promo-active.svg";
import depositImg from "@/assets/supergol/nav/deposit.svg";
import depositActiveImg from "@/assets/supergol/nav/deposit-active.svg";
import withdrawImg from "@/assets/supergol/nav/withdraw.svg";
import withdrawActiveImg from "@/assets/supergol/nav/withdraw-active.svg";
import profileImg from "@/assets/supergol/nav/profile.svg";
import profileActiveImg from "@/assets/supergol/nav/profile-active.svg";

/**
 * 后台未配 / 配置无效时的内置 5 项（supergol 出厂顺序：Inicio · Promoção · Depósito(凸起) · Saque · Perfil）。
 * 每项两张图：image=未选中灰图，imageActive=选中金图；deposit 两张同图（金圆底由 SCSS 画）。
 * 没有 vip / agent-center 的内置图：这两项只有后台配了（自带图）才会出现，图挂了回落到 promo 图。
 */
/** navKey → 路由（只给登录门禁判断用；deposit 是弹层无路由） */
const NAV_ROUTE_BY_KEY: Partial<Record<BottomNavKey, string>> = {
  home: ROUTES.HOME,
  vip: ROUTES.VIP,
  event: ROUTES.EVENT_CENTER,
  withdraw: ROUTES.WITHDRAW,
  profile: ROUTES.PROFILE,
};

const FALLBACK_ITEMS: {
  navKey: BottomNavKey;
  image: string;
  imageActive: string;
}[] = [
  { navKey: "home", image: homeImg, imageActive: homeActiveImg },
  { navKey: "event", image: promoImg, imageActive: promoActiveImg },
  { navKey: "deposit", image: depositImg, imageActive: depositActiveImg },
  { navKey: "withdraw", image: withdrawImg, imageActive: withdrawActiveImg },
  { navKey: "profile", image: profileImg, imageActive: profileActiveImg },
];

/** navKey → 内置彩图，供后台图加载失败时逐项回落（见文件头注释第 3 条） */
const FALLBACK_IMAGE_BY_KEY: Record<BottomNavKey, string> = {
  ...(Object.fromEntries(
    FALLBACK_ITEMS.map(({ navKey, imageActive }) => [navKey, imageActive])
  ) as Record<BottomNavKey, string>),
  vip: promoActiveImg,
  "agent-center": promoActiveImg,
};

/** navKey → i18n key（文案不走后台配置，见文件头注释） */
const LABEL_I18N_KEY: Record<BottomNavKey, string> = {
  home: "navigation.sg.home",
  event: "navigation.sg.promo",
  "agent-center": "agent.invite",
  vip: "vip.rewards",
  profile: "navigation.sg.profile",
  deposit: "navigation.sg.deposit",
  withdraw: "navigation.sg.withdraw",
};

export const BottomNavigation: React.FC<BottomNavigationProps> = memo(
  ({
    className = "",
    activeItem = "home",
    onItemClick,
  }: BottomNavigationProps) => {
    const t = useT();
    const [vipBadgeCount] = useGlobalValue("vipBadgeCount");
    // 后台「首页布局配置 → 底栏」配满 5 项（navKey 可含 deposit / withdraw）就按后台的顺序与图标渲染，
    // 否则用内置的 supergol 5 项；等配置落定后再出现，避免先内置后换皮的闪烁。
    const configuredItems = useHomeBottomNavItems();
    const backgroundImage = useHomeBottomNavBackground();
    const configSettled = useHomeConfigSettled();
    // 第 3 位凸起是本套版式的一部分，恒开（后台的 bottomNavCenterIconEnlarged 只对 template2 生效）
    const centerIconEnlarged = true;

    // 底栏固定常驻（产品要求：竞品 ion-tab-bar 没有划入动画，首屏直接就在）；configSettled 仅保留给上面的配置判定
    void configSettled;

    // VIP 红点计数（首屏取值 + 域名容灾软恢复）统一在共享 hook 里，
    // 两套底栏模板逻辑逐字相同，分散写会漏改其中一个。
    useVipBadgeCount();
    const { messageUnreadCount } = useMessageNotification();

    const isAuthenticated = useUserStore(state => state.isAuthenticated());
    const handleItemClick = useCallback(
      (item: NavigationItem) => {
        // 必须登录的入口（navigation 轴 loginRequiredRoutes，如 supergol 的 Perfil）：未登录只拉起登录弹层，不导航
        const target = NAV_ROUTE_BY_KEY[item.id as BottomNavKey];
        if (
          target &&
          !isAuthenticated &&
          navigationPolicy.loginRequiredRoutes?.includes(target)
        ) {
          openLoginRegisterPopup({ defaultMode: "login", from: target });
          return;
        }
        item.onClick?.();
        onItemClick?.(item.id);
      },
      [onItemClick, isAuthenticated]
    );

    /**
     * 最终渲染项：后台配了恰好 5 项就用后台的（顺序由 sort 决定），否则用内置 5 项。
     * 后台两张图分工：imageUrl=未点击态（必填）、activeImageUrl=点击态（可选）——
     * 没下发点击态就让 imageActive 回落到未点击那张，即选中不换图、一直显示未点击图。
     * 内置项按 afun 原样给两张，选中时换图。
     * badge 只挂 vip 项（可领数量），与通用套一致。
     */
    const navigationItems = useMemo(() => {
      const source =
        configuredItems.length > 0
          ? configuredItems.map(item => {
              const src = utils.getImageUrl(item.imageUrl);
              return {
                navKey: item.navKey,
                image: src,
                imageActive: item.activeImageUrl
                  ? utils.getImageUrl(item.activeImageUrl)
                  : src,
              };
            })
          : FALLBACK_ITEMS;

      return source.map(({ navKey, image, imageActive }) => ({
        id: navKey,
        name: t(LABEL_I18N_KEY[navKey]),
        // icon 是共享契约里的兼容字段（通用套用它选 Lottie），本套不消费，填 navKey 占位
        icon: navKey,
        iconDefault: image,
        iconActive: imageActive,
        badge:
          navKey === "vip" && vipBadgeCount > 0
            ? String(vipBadgeCount)
            : undefined,
        // 红点（无数字）：Promoção = 有可领奖励；Perfil = 有未读站内信
        dot:
          (navKey === "event" && vipBadgeCount > 0) ||
          (navKey === "profile" && messageUnreadCount > 0),
      }));
    }, [configuredItems, t, vipBadgeCount, messageUnreadCount]);

    const navClass = [
      "bottom-navigation",
      // 第 3 项放大/凸起的整套样式挂在这个修饰符下（见 SCSS），关掉就是 5 项等大
      centerIconEnlarged ? "bottom-navigation--center-enlarged" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // 背景图走内联 style（地址是运行时下发的，进不了 SCSS）；铺法在 SCSS 里定。
    // 未配置时不给 style，让整条栏保持 afun 现状（无背景、透出页面底色）。
    const navStyle = backgroundImage
      ? { backgroundImage: `url("${backgroundImage}")` }
      : undefined;

    // 放在所有 hooks 之后再提前返回，避免 hooks 顺序变化
    if (!configSettled) return null;

    return (
      <nav className={navClass} style={navStyle}>
        <div className="bottom-navigation__container">
          {navigationItems.map(item => {
            const isActive = activeItem === item.id;

            return (
              <button
                key={item.id}
                className={["bottom-navigation__item", isActive ? "active" : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleItemClick(item)}
                aria-label={item.name}
                aria-current={isActive ? "page" : undefined}
              >
                <div className="bottom-navigation__icon">
                  {/* 选中换图（与 afun 同一机制）；后台配置项两者同图，等于不换 */}
                  <img
                    className="bottom-navigation__icon-img"
                    src={
                      (isActive ? item.iconActive : item.iconDefault) as string
                    }
                    alt=""
                    draggable={false}
                    // 后台图挂了（404 / 防盗链）就换成内置同 navKey 的 afun 图，
                    // 单项降级而非整栏回落 —— 整栏回落会让「只有一张图坏了」变成 5 项全变。
                    // 判据用「当前 src 是不是已经是内置图」而不是打一次性标记：内置图是打进
                    // bundle 的、不会再失败，故它天然终止循环；而标记法会在 React 之后又把 src
                    // 改回后台地址（切换选中态时）之后失去回落能力。
                    onError={e => {
                      const img = e.currentTarget;
                      const builtin =
                        FALLBACK_IMAGE_BY_KEY[item.id as BottomNavKey];
                      if (builtin && !img.src.endsWith(builtin)) {
                        img.src = builtin;
                      }
                    }}
                  />
                </div>
                <span className="bottom-navigation__label">{item.name}</span>
                {item.badge && item.badge !== "0" && (
                  <span className="bottom-navigation__badge">{item.badge}</span>
                )}
                {!item.badge && item.dot && (
                  <span className="bottom-navigation__dot" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }
);

BottomNavigation.displayName = "BottomNavigation";

export default BottomNavigation;
